import { useEffect, useRef, useState } from 'react';

const TARGET_KISSES = 5;
const UP_MS = 1500;
const BETWEEN_MS_MIN = 550;
const BETWEEN_MS_MAX = 900;
const KISS_SHOW_MS = 420;

const HOLES = [0, 1, 2, 3];

let squeakAudioContext;

const getSqueakAudioContext = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;

  if (!squeakAudioContext) {
    squeakAudioContext = new AudioContext();
  }

  if (squeakAudioContext.state === 'suspended') {
    squeakAudioContext.resume().catch(() => {});
  }

  return squeakAudioContext;
};

function playKissSqueak() {
  const context = getSqueakAudioContext();
  if (!context) return;

  const play = () => {
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    // Tiny upward chirp — cute mousey squeak.
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(780, now);
    oscillator.frequency.exponentialRampToValueAtTime(1480, now + 0.055);
    oscillator.frequency.exponentialRampToValueAtTime(1100, now + 0.11);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.11, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.16);
  };

  if (context.state === 'suspended') {
    context.resume().then(play).catch(() => {});
    return;
  }

  play();
}

function KissAMoleGame({
  active = true,
  locked = false,
  targetKisses = TARGET_KISSES,
  onWin,
  onMiss,
  levelKey,
}) {
  const [kisses, setKisses] = useState(0);
  const [activeHole, setActiveHole] = useState(null);
  const [kissedHole, setKissedHole] = useState(null);

  const kissesRef = useRef(0);
  const activeHoleRef = useRef(null);
  const lastHoleRef = useRef(null);
  const wonRef = useRef(false);
  const missLockRef = useRef(false);
  const spawnPendingRef = useRef(false);
  const activeRef = useRef(active);
  const lockedRef = useRef(locked);
  const targetRef = useRef(Number(targetKisses) || TARGET_KISSES);
  const onWinRef = useRef(onWin);
  const onMissRef = useRef(onMiss);
  const timersRef = useRef([]);
  const apiRef = useRef({});

  activeRef.current = active;
  lockedRef.current = locked;
  targetRef.current = Number(targetKisses) || TARGET_KISSES;
  onWinRef.current = onWin;
  onMissRef.current = onMiss;

  apiRef.current.clearTimers = () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    spawnPendingRef.current = false;
  };

  apiRef.current.schedule = (fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  };

  apiRef.current.canPlay = () =>
    activeRef.current &&
    !lockedRef.current &&
    !wonRef.current &&
    !missLockRef.current;

  apiRef.current.pickHole = () => {
    const options = HOLES.filter((hole) => hole !== lastHoleRef.current);
    const pool = options.length ? options : HOLES;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  apiRef.current.duckMiss = (hole) => {
    if (activeHoleRef.current !== hole || wonRef.current || missLockRef.current) return;
    activeHoleRef.current = null;
    setActiveHole(null);
    missLockRef.current = true;
    onMissRef.current?.();
  };

  apiRef.current.spawnMole = () => {
    spawnPendingRef.current = false;
    if (!apiRef.current.canPlay() || activeHoleRef.current !== null) return;

    const hole = apiRef.current.pickHole();
    lastHoleRef.current = hole;
    activeHoleRef.current = hole;
    setActiveHole(hole);
    setKissedHole(null);

    apiRef.current.schedule(() => apiRef.current.duckMiss(hole), UP_MS);
  };

  apiRef.current.queueSpawn = (delay) => {
    if (spawnPendingRef.current || activeHoleRef.current !== null || wonRef.current) return;
    spawnPendingRef.current = true;
    apiRef.current.schedule(() => {
      spawnPendingRef.current = false;
      if (!apiRef.current.canPlay()) return;
      apiRef.current.spawnMole();
    }, delay);
  };

  useEffect(() => {
    apiRef.current.clearTimers();
    kissesRef.current = 0;
    activeHoleRef.current = null;
    lastHoleRef.current = null;
    wonRef.current = false;
    missLockRef.current = false;
    setKisses(0);
    setActiveHole(null);
    setKissedHole(null);
  }, [levelKey]);

  useEffect(() => {
    if (locked) return undefined;

    missLockRef.current = false;
    if (!active || wonRef.current) return undefined;
    if (activeHoleRef.current !== null || spawnPendingRef.current) return undefined;

    apiRef.current.queueSpawn(BETWEEN_MS_MIN);
    return undefined;
  }, [active, locked, levelKey]);

  useEffect(
    () => () => {
      apiRef.current.clearTimers();
    },
    []
  );

  const handleKiss = (hole) => {
    if (!apiRef.current.canPlay() || activeHoleRef.current !== hole) return;

    apiRef.current.clearTimers();
    playKissSqueak();
    activeHoleRef.current = null;
    setActiveHole(null);
    setKissedHole(hole);

    const next = kissesRef.current + 1;
    kissesRef.current = next;
    setKisses(next);

    if (next >= targetRef.current) {
      wonRef.current = true;
      apiRef.current.schedule(() => {
        setKissedHole(null);
        onWinRef.current?.();
      }, KISS_SHOW_MS);
      return;
    }

    apiRef.current.schedule(() => {
      setKissedHole(null);
      if (!apiRef.current.canPlay()) return;
      const gap = BETWEEN_MS_MIN + Math.random() * (BETWEEN_MS_MAX - BETWEEN_MS_MIN);
      apiRef.current.queueSpawn(gap);
    }, KISS_SHOW_MS);
  };

  const goal = Number(targetKisses) || TARGET_KISSES;

  return (
    <div className="kiss-a-mole">
      <p className="question-text">Kiss-a-Mole</p>
      <p className="kiss-a-mole-hint">
        Kiss {goal} moles! {kisses} / {goal}
      </p>
      <div className="kiss-a-mole-field" aria-label="Kiss-a-Mole board">
        {HOLES.map((hole) => {
          const isUp = activeHole === hole;
          const isKissed = kissedHole === hole;

          return (
            <div key={hole} className="kiss-a-mole-quadrant">
              <div className={`kiss-a-mole-hole ${isUp || isKissed ? 'kiss-a-mole-hole-active' : ''}`}>
                <button
                  type="button"
                  className={`kiss-a-mole-mole ${isUp ? 'is-up' : ''} ${isKissed ? 'is-kissed' : ''}`}
                  aria-label={isUp ? 'Kiss the mole' : 'Empty hole'}
                  disabled={locked || !isUp}
                  onClick={() => handleKiss(hole)}
                >
                  <span className="kiss-a-mole-body" aria-hidden="true">
                    <span className="kiss-a-mole-ear kiss-a-mole-ear-left" />
                    <span className="kiss-a-mole-ear kiss-a-mole-ear-right" />
                    <span className="kiss-a-mole-face">
                      <span className="kiss-a-mole-eye kiss-a-mole-eye-left" />
                      <span className="kiss-a-mole-eye kiss-a-mole-eye-right" />
                      <span className="kiss-a-mole-nose" />
                    </span>
                  </span>
                  {isKissed ? (
                    <span className="kiss-a-mole-heart" aria-hidden="true">
                      ♥
                    </span>
                  ) : null}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default KissAMoleGame;
